/**
 * FreedrIQSplash — first-visit modal shown on the Learning board asking new
 * learners to take the FreedrIQ Free entry assessment. Fully skippable — a
 * dismissed choice is remembered so we never nag the same user twice.
 *
 * Detection order (any one wins → skip modal):
 *   1. localStorage["freedriq:completed"] OR localStorage["freedriq:dismissed"]
 *   2. GET /api/freedriq/my-latest?tier=free&phase=pre  →  responses.length > 0
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Sparkles, GraduationCap, Timer, ShieldCheck } from 'lucide-react';

const LS_KEY_COMPLETED = 'freedriq:completed';
const LS_KEY_DISMISSED = 'freedriq:dismissed';

const FreedrIQSplash = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Local flags win — no API call needed.
    try {
      if (localStorage.getItem(LS_KEY_COMPLETED)) return;
      if (localStorage.getItem(LS_KEY_DISMISSED)) return;
    } catch { /* ignore */ }
    // Server check.
    axios.get(`${API}/freedriq/my-latest?tier=free&phase=pre`)
      .then((r) => {
        const rows = r.data?.responses || [];
        if (rows.length === 0) setOpen(true);
        else try { localStorage.setItem(LS_KEY_COMPLETED, '1'); } catch { /* ignore */ }
      })
      .catch(() => { /* if the endpoint fails silently, don't nag */ });
  }, [API, user]);

  const dismiss = () => {
    try { localStorage.setItem(LS_KEY_DISMISSED, '1'); } catch { /* ignore */ }
    setOpen(false);
  };
  const takeIt = () => {
    setOpen(false);
    navigate('/freedriq?tier=free&phase=pre');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="max-w-lg" data-testid="freedriq-splash">
        <div className="eyebrow text-primary inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> FreedrIQ · Free entry
        </div>
        <DialogTitle className="font-heading text-2xl font-extrabold mt-1">
          Set your FreezeDryIQ baseline
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
          Two short sections — a 7-question self-assessment, then a 25-question knowledge test —
          give Prof. FreedrIQ everything it needs to personalise your first learning path.
        </DialogDescription>

        <div className="grid grid-cols-3 gap-3 mt-2">
          <div className="rounded-lg border border-border p-3">
            <Timer className="w-4 h-4 text-primary" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-1.5">Time</div>
            <div className="text-xs font-bold">~8 min</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <GraduationCap className="w-4 h-4 text-primary" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-1.5">Skill map</div>
            <div className="text-xs font-bold">6 areas</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-1.5">Not graded</div>
            <div className="text-xs font-bold">Baseline only</div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={dismiss} className="rounded-full font-bold" data-testid="freedriq-splash-skip">
            Skip for now
          </Button>
          <Button onClick={takeIt} className="rounded-full font-bold" data-testid="freedriq-splash-take">
            Take assessment <Sparkles className="ml-1.5 w-3.5 h-3.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FreedrIQSplash;
